(ns ductile.ui.agent.artifacts.vehicle
  (:require [clojure.string :as str]
            [ductile.time.format :as time-format]
            [ductile.ui.components :as ui]
            [ductile.ui.fahrzeug :as fahrzeug]
            [ductile.ui.locale :refer [tr]]))

(def ^:private details-pattern
  [:vehicle/vin
   :ars/id
   :contract/names
   :vehicle/engine-type
   :fuel/type
   :make/name
   :vehicle/model
   :color/description
   :external.bmw/color-code
   :external.bmw/usage
   :vehicle/dimensions
   :vehicle/weight
   :vehicle/new?
   :vehicle/transport-mode-active?
   :vehicle/dispatchable-without-call-off?
   :dispo/available-datetime
   :fahrzeug/storage-care-start-datetime
   :vehicle/equipment
   :vehicle/tires
   :vehicle/rims
   :vehicle/extra-tires
   :vehicle/extra-rims
   :bca/vehicle-class
   :car-on-sale/vehicle-class])

(defn- entity-from
  [{:keys [payload]} dataset-data]
  (let [dataset (some-> payload :dataset-id dataset-data)]
    (or (:fahrzeug payload)
        (when (map? dataset) dataset)
        (when (and (sequential? dataset) (map? (first dataset)))
          (first dataset)))))

(defn view
  [artifact {:keys [dataset-data error-view]}]
  (let [fahrzeug (entity-from artifact dataset-data)]
    (if (map? fahrzeug)
      [:div.space-y-6
       [fahrzeug/fahrzeug-header fahrzeug]
       (letfn [(phase-tone [phase]
                 (let [done? (case phase
                               :entry (some? (:entry/datetime fahrzeug))
                               :exit (some? (:exit/datetime fahrzeug))
                               false)]
                   (if done?
                     (if (= phase :entry)
                       {:text "text-blue-700" :bg "bg-blue-100" :icon :check}
                       {:text "text-yellow-700" :bg "bg-yellow-100" :icon :check})
                     {:text "text-slate-500" :bg "bg-slate-100" :icon :clock})))
               (phase-title [phase]
                 (if (= phase :entry)
                   (if-let [dt (:entry/datetime fahrzeug)]
                     (str (str/capitalize (tr "Entered")) ": " (time-format/->str {:format :datetime} dt))
                     (tr "not entered yet"))
                   (if-let [dt (:exit/datetime fahrzeug)]
                     (str (str/capitalize (tr "Exited")) ": " (time-format/->str {:format :datetime} dt))
                     (tr "not exited yet"))))
               (cell [v]
                 (if (nil? v)
                   [:span.text-slate-400 "None"]
                   [fahrzeug/inspect-value v]))
               (kv-row [label value]
                 [:tr.border-b.last:border-b-0.align-top
                  [:th.text-left.font-normal.text-slate-600.py-2.pr-3.pl-4 {:class "w-[220px]"} label]
                  [:td.py-2.text-slate-800.text-sm [cell value]]])
               (kv-row-node [label node]
                 [:tr.border-b.last:border-b-0.align-top
                  [:th.text-left.font-normal.text-slate-600.py-2.pr-3.pl-4 {:class "w-[220px]"} label]
                  [:td.py-2.text-slate-800.text-sm node]])
               (phase-card [phase]
                 (let [entry? (= phase :entry)
                       exit? (= phase :exit)
                       order (if entry? (:entry/transport-order fahrzeug) (:exit/transport-order fahrzeug))
                       transport (if entry? (:entry/transport fahrzeug) (:exit/transport fahrzeug))
                       expected-at (if entry?
                                     (:entry/expected-datetime fahrzeug)
                                     (:fahrzeug/expected-customer-delivery-datetime fahrzeug))
                       expected-label (if entry?
                                        (tr :entry/expected-datetime)
                                        (tr :fahrzeug/expected-customer-delivery-datetime))
                       tone (phase-tone phase)]
                   [:div.border.border-slate-300.rounded-lg.overflow-hidden.bg-white.shadow-sm
                    [:div.px-4.py-3.border-b.border-slate-300.flex.items-center.gap-2
                     {:class [(:bg tone) (:text tone)]}
                     [ui/icon {:size 18} (:icon tone)]
                     [:h3.text-sm.font-bold.leading-none.mt-0 {:class (:text tone)} (phase-title phase)]]
                    [:div
                     [:div.px-4.py-2.uppercase.tracking-wider.font-bold.text-xs.border-b.leading-none.bg-slate-50
                      (tr "Transport Order")]
                     [:table.w-full.text-sm
                      [:tbody
                       (when-let [forwarder (get-in order [:transport/forwarder :address/name])]
                         [kv-row (tr :transport/forwarder) forwarder])
                       (when expected-at
                         [kv-row expected-label (time-format/->str {:format :datetime} expected-at)])
                       (when (:exit/expected-datetime fahrzeug)
                         [kv-row (tr "Expected departure")
                          (time-format/->str {:format :date} (:exit/expected-datetime fahrzeug))])
                       (when (and exit? (:fahrzeug/destination fahrzeug))
                         [kv-row (tr :ductile.workflow/dispatchable?)
                          (cond-> (tr (if (:ductile.workflow/dispatchable? fahrzeug) "Yes" "No"))
                            (:dispo/available-datetime fahrzeug)
                            (str ": " (time-format/->str {:format :datetime} (:dispo/available-datetime fahrzeug))))])
                       (when-let [loc (:origin/location order)]
                         [kv-row-node (tr "Origin") [fahrzeug/location loc]])
                       (when-let [loc (:destination/location order)]
                         [kv-row-node (tr "Destination") [fahrzeug/location loc]])
                       (when (and exit?
                                  (:fahrzeug/destination fahrzeug)
                                  (not= (:fahrzeug/destination fahrzeug) (:destination/location order)))
                         [kv-row-node (tr "Compound override destination")
                          [fahrzeug/location (:fahrzeug/destination fahrzeug)]])
                       (when-let [name (:transport/name order)]
                         [kv-row (tr "Transport name") name])
                       (when-let [mode (:transport/mode order)]
                         [kv-row (tr "Transport mode") (fahrzeug/transport-mode mode)])
                       (when (and exit? (:fahrzeug/exit-delivery-datetime fahrzeug))
                         [kv-row (tr "Delivery")
                          (time-format/->str (:fahrzeug/exit-delivery-datetime fahrzeug))])
                       (when (and exit? (:fahrzeug/customs-number fahrzeug))
                         [kv-row (tr "Customs number") (:fahrzeug/customs-number fahrzeug)])]]
                     [:div.px-4.py-2.uppercase.tracking-wider.font-bold.text-xs.border-t.border-b.leading-none.bg-slate-50
                      (tr "Transport")]
                     [:table.w-full.text-sm
                      [:tbody
                       (when-let [name (:transport/name transport)]
                         [kv-row (tr "Transport name") name])
                       (when-let [mode (fahrzeug/transport-mode
                                        (or (if entry?
                                              (:entry/mode fahrzeug)
                                              (:exit/mode fahrzeug))
                                            (:transport/mode transport)))]
                         [kv-row (tr "Transport mode") mode])
                       (when-let [truck-number (-> transport :transport/transporter :truck/number)]
                         [kv-row (tr "Truck number") truck-number])
                       (when-let [carrier (:transport/carrier transport)]
                         [kv-row (tr "Carrier") ((some-fn :address/name :company/name) carrier)])
                       (when-let [plate (:transport/license-plate transport)]
                         [kv-row (tr "License plate") plate])
                       (when-let [load-id (:load/id transport)]
                         [kv-row (tr "Load ID") load-id])
                       (when-let [load-no (:transport/load-number transport)]
                         [kv-row (tr "Load #") load-no])]]]]))]
         [:div.space-y-4
          [phase-card :entry]
          [phase-card :exit]])
       [fahrzeug/section :section/active-services
        [fahrzeug/section-header :section/active-services
         [fahrzeug/items-count (:workflow/active-services fahrzeug)
          ["%1 active service" "%1 active services"]]]
        [fahrzeug/listing {:context (select-keys fahrzeug [:ductile/id])}
         (:workflow/active-services fahrzeug)]]
       [fahrzeug/section :section/scheduled-services
        [fahrzeug/section-header :section/scheduled-services
         [fahrzeug/items-count (:workflow/scheduled-services fahrzeug)
          ["%1 scheduled service" "%1 scheduled services"]]]
        [fahrzeug/listing (:workflow/scheduled-services fahrzeug)]]
       [fahrzeug/section :section/completed-services
        [fahrzeug/section-header :section/completed-services
         [fahrzeug/items-count (:service.event/completed fahrzeug)
          ["%1 completed service" "%1 completed services"]]]
        [fahrzeug/listing (:service.event/completed fahrzeug)]]
       [fahrzeug/section :section/accessories
        [fahrzeug/section-header :section/accessories]
        [fahrzeug/listing (:vehicle/accessories fahrzeug)]]
       [fahrzeug/section :section/details
        [fahrzeug/section-header :section/details]
        [fahrzeug/entity-view
         {:ui/layout :layout/standalone
          :pull/pattern details-pattern}
         fahrzeug]]
       [fahrzeug/section :section/images
        [fahrzeug/section-header :section/images]
        [fahrzeug/listing
         (->> (concat (fahrzeug/services-with-photo-comments fahrzeug)
                      (fahrzeug/services-with-photo-data fahrzeug))
              (mapv #(select-keys % (conj fahrzeug/photo-attrs :service/code :ductile/comments))))]]
       [fahrzeug/section :section/notifications
        [fahrzeug/section-header :section/notifications]
        [fahrzeug/listing (concat (:ductile/notifications fahrzeug)
                                  (mapcat :ductile/notifications (:fahrzeug/services fahrzeug)))]]]
      [error-view {:error-message "Fahrzeug artifact payload requires :fahrzeug map."}])))
